// Utility buttons at the bottom of the rail: Personal Tools pinned from the account menu, Create (round +,
// opens the Create menu, which also opens the notepad) and my account menu. Icon-only to keep the rail
// short; hover hints name them and their menus open to the right.
import { useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday } from 'date-fns';
import {
  Bell,
  BellOff,
  Briefcase,
  CircleCheck,
  FileText,
  Grid3x3,
  Hash,
  Keyboard,
  ListTodo,
  LogOut,
  Monitor,
  Moon,
  NotebookPen,
  Palette,
  Pin,
  Plus,
  Settings,
  SmilePlus,
  SquarePen,
  Sun,
  Timer,
  UserPlus,
  Users,
  X,
  type IconProps,
} from '@/components/ui/icons';
import { useDb, useMe, signOut, mutate, patch } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { useUi, applyTheme, notificationsMuted, NOTIFY_MUTED_FOREVER } from '@/lib/ui';
import { spacesOf } from '@/lib/hierarchy';
import { createDoc } from '@/lib/actions';
import { Popover } from '@/components/ui/Popover';
import { Avatar } from '@/components/ui/Avatar';
import { Tooltip } from '@/components/ui/Tooltip';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { Notepad } from './Notepad';
import { InviteModal } from './InviteModal';
import { ShortcutsModal } from './ShortcutsModal';
import { openDialog } from './dialogs';
import { useIsGuest } from '@/lib/roles';

interface CreateOption {
  key: string;
  label: string;
  desc: string;
  icon: React.ReactNode;
  /** theme token for the icon's circle */
  color: string;
  disabled?: boolean;
  run: () => void;
}

function CreateMenu({ close, onInvite, onNote }: { close: () => void; onInvite: () => void; onNote: () => void }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const firstSpace = useDb((db) => spacesOf(db, ws)[0]?.id);
  const guest = useIsGuest();
  const all: CreateOption[] = [
    { key: 'task', label: 'Task', desc: 'Add a task to any list', icon: <CircleCheck size={18} />, color: 'var(--accent)', run: () => useUi.setState({ createTaskOpen: true }) },
    { key: 'message', label: 'Message', desc: 'Start a direct conversation', icon: <SquarePen size={18} />, color: 'var(--link)', run: () => navigate(`/${ws}/chat?new=dm`) },
    { key: 'channel', label: 'Channel', desc: 'Start a group conversation by topic', icon: <Hash size={18} />, color: 'var(--text-2)', run: () => navigate(`/${ws}/chat?new=channel`) },
    {
      key: 'doc',
      label: 'Doc',
      desc: 'Write and share a document',
      icon: <FileText size={18} />,
      color: 'var(--success)',
      run: () => navigate(`/${ws}/docs/${createDoc(ws, null).doc.id}`),
    },
    {
      key: 'list',
      label: 'List',
      desc: firstSpace ? 'Track and manage work in a space' : 'Create a space first',
      icon: <ListTodo size={18} />,
      color: 'var(--warning)',
      disabled: !firstSpace,
      run: () => firstSpace && openDialog({ kind: 'list', spaceId: firstSpace, folderId: null }),
    },
    { key: 'space', label: 'Space', desc: "Organize a team's projects and lists", icon: <Grid3x3 size={18} />, color: 'var(--text-2)', run: () => openDialog({ kind: 'space' }) },
    { key: 'note', label: 'Note', desc: 'Jot something down in your notepad', icon: <NotebookPen size={18} />, color: 'var(--text-2)', run: onNote },
  ];
  // guests only add tasks to what's shared with them, message people and keep notes
  const options = guest ? all.filter((o) => o.key === 'task' || o.key === 'message' || o.key === 'note') : all;
  return (
    <Menu className="create-menu">
      <div className="create-menu-title">Create</div>
      {options.map((o) => (
        <button key={o.key} type="button" role="menuitem" className="create-item" disabled={o.disabled} onClick={() => (close(), o.run())}>
          <span className="create-item-icon" style={{ '--c': o.color } as React.CSSProperties}>
            {o.icon}
          </span>
          <span className="create-item-text">
            <span className="create-item-name">{o.label}</span>
            <span className="create-item-desc">{o.desc}</span>
          </span>
        </button>
      ))}
      {!guest && (
        <>
          <MenuSeparator />
          <MenuItem icon={<UserPlus size={16} />} label="Invite people" onClick={() => (close(), onInvite())} />
        </>
      )}
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// Personal Tools: listed in the account menu; pinned ones also sit in the rail (per device)

interface Tool {
  key: string;
  label: string;
  Icon: ComponentType<IconProps>;
  run: () => void;
}

function useTools(openNote: () => void): Tool[] {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  return [
    { key: 'task', label: 'Create task', Icon: CircleCheck, run: () => useUi.setState({ createTaskOpen: true }) },
    { key: 'my-work', label: 'My Work', Icon: Briefcase, run: () => navigate(`/${ws}/home`) },
    { key: 'time', label: 'Track Time', Icon: Timer, run: () => navigate(`/${ws}/timesheets`) },
    { key: 'notepad', label: 'Notepad', Icon: NotebookPen, run: openNote },
    { key: 'doc', label: 'Create Doc', Icon: FileText, run: () => navigate(`/${ws}/docs/${createDoc(ws, null).doc.id}`) },
    { key: 'people', label: 'View People', Icon: Users, run: () => navigate(`/${ws}/teams`) },
  ];
}

function togglePin(key: string) {
  useUi.setState((s) => ({ pinnedTools: s.pinnedTools.includes(key) ? s.pinnedTools.filter((k) => k !== key) : [...s.pinnedTools, key] }));
}

// ---------------------------------------------------------------------------
// account menu

/** "Set status": a custom message others see on your profile card. */
export function StatusField() {
  const me = useMe();
  const status = useDb((db) => db.users[me]?.status ?? '');
  const [draft, setDraft] = useState<string | null>(null);
  const save = (value: string) => {
    const next = value.trim().slice(0, 100) || null;
    if (next !== (status || null)) mutate([patch('users', me, { status: next })]);
    setDraft(null);
  };
  if (draft !== null)
    return (
      <div className="acct-status editing">
        <SmilePlus size={16} />
        <input
          autoFocus
          value={draft}
          maxLength={100}
          placeholder="What's your status?"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => save(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save(draft);
            if (e.key === 'Escape') {
              e.stopPropagation(); // cancel the edit, keep the menu open
              setDraft(null);
            }
          }}
        />
      </div>
    );
  return (
    <div className="acct-status" role="button" tabIndex={0} onClick={() => setDraft(status)} onKeyDown={(e) => e.key === 'Enter' && setDraft(status)}>
      <SmilePlus size={16} />
      <span className={status ? 'grow truncate' : 'grow truncate faint'}>{status || 'Set status'}</span>
      {status && (
        <Tooltip label="Clear status">
          <span
            className="acct-status-clear"
            role="button"
            aria-label="Clear status"
            onClick={(e) => {
              e.stopPropagation();
              save('');
            }}
          >
            <X size={13} />
          </span>
        </Tooltip>
      )}
    </div>
  );
}

function mutedLabel(until: number) {
  if (until >= NOTIFY_MUTED_FOREVER) return 'until you unmute';
  return `until ${format(until, isToday(until) ? 'h:mm a' : 'EEE h:mm a')}`;
}

function MuteMenu({ close }: { close: () => void }) {
  const until = useUi((s) => s.notifyMutedUntil);
  const muted = notificationsMuted({ notifyMutedUntil: until });
  const mute = (ms: number | null) => {
    useUi.setState({ notifyMutedUntil: ms });
    close();
  };
  const tomorrow9 = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  };
  return (
    <Menu style={{ width: 230 }}>
      {muted && until && (
        <>
          <MenuLabel>Muted {mutedLabel(until)}</MenuLabel>
          <MenuItem icon={<Bell size={15} />} label="Unmute" onClick={() => mute(null)} />
          <MenuSeparator />
        </>
      )}
      <MenuItem label="For 30 minutes" onClick={() => mute(Date.now() + 30 * 60_000)} />
      <MenuItem label="For 1 hour" onClick={() => mute(Date.now() + 60 * 60_000)} />
      <MenuItem label="For 2 hours" onClick={() => mute(Date.now() + 2 * 60 * 60_000)} />
      <MenuItem label="Until tomorrow" hint="9:00 AM" onClick={() => mute(tomorrow9())} />
      <MenuItem label="Until I turn them back on" onClick={() => mute(NOTIFY_MUTED_FOREVER)} />
    </Menu>
  );
}

function ThemeMenu() {
  const theme = useUi((s) => s.theme);
  const setTheme = (t: 'dark' | 'light' | 'system') => {
    useUi.setState({ theme: t });
    applyTheme(t);
  };
  return (
    <Menu>
      <MenuItem icon={<Moon size={15} />} label="Dark" checked={theme === 'dark'} onClick={() => setTheme('dark')} />
      <MenuItem icon={<Sun size={15} />} label="Light" checked={theme === 'light'} onClick={() => setTheme('light')} />
      <MenuItem icon={<Monitor size={15} />} label="Auto" checked={theme === 'system'} onClick={() => setTheme('system')} />
    </Menu>
  );
}

function AccountMenu({ close, tools }: { close: () => void; tools: Tool[] }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const meUser = useDb((db) => db.users[me]);
  const navigate = useNavigate();
  const until = useUi((s) => s.notifyMutedUntil);
  const muted = notificationsMuted({ notifyMutedUntil: until });
  const pinned = useUi((s) => s.pinnedTools);
  return (
    <Menu className="acct-menu">
      <div className="acct-head">
        <Avatar userId={me} size={40} showPresence />
        <div className="grow">
          <div className="acct-name truncate">{meUser?.name}</div>
          <div className="acct-sub truncate">{muted && until ? `Online · notifications muted ${mutedLabel(until)}` : 'Online'}</div>
        </div>
      </div>
      <StatusField />
      <MenuItem icon={muted ? <BellOff size={15} /> : <Bell size={15} />} label={muted ? 'Notifications muted' : 'Mute notifications'} submenu={() => <MuteMenu close={close} />} />
      <MenuSeparator />
      <MenuItem icon={<Settings size={15} />} label="Settings" onClick={() => (close(), navigate(`/${ws}/settings`))} />
      <MenuItem icon={<Bell size={15} />} label="Notifications" onClick={() => (close(), navigate(`/${ws}/settings?tab=notifications`))} />
      <MenuItem icon={<Palette size={15} />} label="Themes" submenu={() => <ThemeMenu />} />
      <MenuItem icon={<Keyboard size={15} />} label="Keyboard shortcuts" shortcut="?" onClick={() => (close(), useUi.setState({ shortcutsOpen: true }))} />
      <MenuSeparator />
      <MenuLabel>Personal Tools</MenuLabel>
      {tools.map((t) => {
        const on = pinned.includes(t.key);
        return (
          <MenuItem
            key={t.key}
            className="acct-tool"
            icon={<t.Icon size={15} />}
            label={t.label}
            onClick={() => (close(), t.run())}
            right={
              <Tooltip label={on ? 'Unpin from sidebar' : 'Pin to sidebar'}>
                <span
                  className={on ? 'acct-pin on' : 'acct-pin'}
                  role="button"
                  aria-label={on ? `Unpin ${t.label}` : `Pin ${t.label}`}
                  aria-pressed={on}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePin(t.key);
                  }}
                >
                  <Pin size={14} fill={on ? 'currentColor' : undefined} />
                </span>
              </Tooltip>
            }
          />
        );
      })}
      <MenuSeparator />
      <MenuItem
        icon={<LogOut size={15} />}
        label="Log out"
        onClick={() => {
          close();
          window.atriumDesktop?.setBadgeCount(0);
          void signOut();
          navigate('/login');
        }}
      />
    </Menu>
  );
}

export function RailTools() {
  const me = useMe();
  const name = useDb((db) => db.users[me]?.name ?? 'Account');
  const [invite, setInvite] = useState(false);
  const [note, setNote] = useState(false);
  const tools = useTools(() => setNote(true));
  const pinned = useUi((s) => s.pinnedTools);
  const muted = useUi((s) => notificationsMuted(s));
  return (
    <div className="rail-tools">
      {tools
        .filter((t) => pinned.includes(t.key))
        .map((t) => (
          <Tooltip key={t.key} label={t.label} placement="right">
            <button className="rail-tool" aria-label={t.label} onClick={t.run}>
              <t.Icon size={20} />
            </button>
          </Tooltip>
        ))}
      {/* the notepad opens from the Create menu or a pinned tool, anchored where the + button is */}
      <div className="rail-create-wrap">
        <Popover placement="right-end" content={(close) => <CreateMenu close={close} onInvite={() => setInvite(true)} onNote={() => setNote(true)} />}>
          <button className="rail-create" aria-label="Create" title="Create">
            <Plus size={22} className="rail-create-plus" />
            <X size={20} className="rail-create-close" />
          </button>
        </Popover>
        <Popover open={note} onOpenChange={setNote} disabled placement="right-end" content={() => <Notepad />}>
          <span className="rail-anchor" aria-hidden />
        </Popover>
      </div>
      {/* menu buttons use a native hint: a tooltip would sit on top of the menu they open */}
      <Popover placement="right-end" content={(close) => <AccountMenu close={close} tools={tools} />}>
        <button className="rail-tool rail-account" aria-label={`${name} — account menu`} title={muted ? `${name} · notifications muted` : name}>
          <Avatar userId={me} size={28} showPresence />
          {muted && (
            <span className="rail-muted" aria-hidden>
              <BellOff size={10} />
            </span>
          )}
        </button>
      </Popover>
      <InviteModal open={invite} onClose={() => setInvite(false)} />
      <ShortcutsModal />
    </div>
  );
}
